import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea'; // For description
import { toast } from 'sonner'; // Assuming you use sonner for toasts
import { Loader2 } from 'lucide-react';

interface CreateCourseDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CreateProductPayload {
  name: string;
  description?: string;
  price: number; // In cents
  currency: string;
}

// Mutation function
const createProduct = async (payload: CreateProductPayload) => {
  const { data, error } = await supabase.functions.invoke('create-stripe-product', {
    body: payload,
  });

  if (error) {
    console.error('Error creating product:', error);
    throw new Error(error.message || 'Failed to create product.');
  }
  // Assuming the function returns the new product/price on success
  // Or maybe just a success status
  return data; 
};

const CreateCourseDialog: React.FC<CreateCourseDialogProps> = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState(''); // Store as string initially for input
  const [currency, setCurrency] = useState('usd'); // Default to USD

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      toast.success('Course created successfully!');
      // Invalidate the query for the product list to refetch
      queryClient.invalidateQueries({ queryKey: ['admin', 'stripeProducts'] });
      onClose(); // Close the dialog
      // Optionally reset form fields
      setName('');
      setDescription('');
      setPrice('');
      setCurrency('usd');
    },
    onError: (error) => {
      toast.error(`Error creating course: ${error.message}`);
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const priceInCents = Math.round(parseFloat(price) * 100); // Convert to cents

    if (!name || !price || isNaN(priceInCents) || priceInCents <= 0) {
      toast.error('Please enter a valid name and price.');
      return;
    }
    if (!currency) {
        toast.error('Please enter a currency code (e.g., usd, eur).');
        return;
    }

    mutation.mutate({
      name,
      description,
      price: priceInCents,
      currency: currency.toLowerCase(),
    });
  };

  // Handle changes for controlled inputs
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only numbers and a single decimal point
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
        setPrice(value);
    }
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only letters, convert to lowercase
    const value = e.target.value.toLowerCase().replace(/[^a-z]/g, '');
    setCurrency(value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Course</DialogTitle>
          <DialogDescription>
            Enter the details for the new course. This will create a product and a price in Stripe.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="col-span-3"
                placeholder="(Optional)"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="price" className="text-right">
                Price
              </Label>
              <Input
                id="price"
                type="text" // Use text to allow decimal input
                inputMode="decimal"
                value={price}
                onChange={handlePriceChange}
                className="col-span-3"
                placeholder="e.g., 99.00"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="currency" className="text-right">
                Currency
              </Label>
              <Input
                id="currency"
                value={currency}
                onChange={handleCurrencyChange}
                className="col-span-3"
                placeholder="e.g., usd, eur"
                maxLength={3} // ISO currency codes are 3 letters
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} 
              Create Course
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCourseDialog;
